import type { AlgorithmOperation } from "@algoquest/schemas";
import { SelectionSortEngine, type SelectionSortDomainState } from "./selection-sort";
import type { SortActionOutcome, SortController, SortDecision, SortViewState } from "./SortController";

/**
 * Phase 08 — wraps SelectionSortEngine behind the shared SortController
 * contract, replacing GameRuntime's old `tryInspectCandidate` /
 * `tryDecideSelection` / `applySelectionSortStateToBoxes` /
 * `updateSelectionComparePointer` methods (their guard logic is
 * reproduced here verbatim, just returning a SortActionOutcome instead
 * of mutating GameRuntime state directly).
 *
 * toView() translates the engine's own domain state into SortViewState:
 * `requiredIndex` is the box being scanned next (`scanIndex`);
 * `compareIndices` is `[currentBestIndex, scanIndex]`, matching the
 * engine's own COMPARE operation's index order; `stickyIndex` is the
 * tracked candidate (`currentBestIndex`), which stays highlighted across
 * an entire scan round even between individual decisions; `shifts` is
 * always 0 (Master Spec §40 tracks shifts only for Insertion Sort).
 */
function toView(state: SelectionSortDomainState): SortViewState {
  return {
    algorithm: "selection-sort",
    values: state.values,
    sortDirection: state.sortDirection,
    requiredIndex: state.scanIndex,
    compareIndices: [state.currentBestIndex, state.scanIndex],
    stickyIndex: state.currentBestIndex,
    awaitingDecision: state.awaitingDecision,
    sortedIndices: state.sortedIndices,
    comparisons: state.comparisons,
    swaps: state.swaps,
    shifts: 0,
    passesCompleted: state.passesCompleted,
    mistakes: state.mistakes,
    isComplete: state.isComplete,
  };
}

export class SelectionSortController implements SortController {
  readonly algorithm = "selection-sort" as const;
  private readonly engine: SelectionSortEngine;

  constructor(values: number[], sortDirection: "ascending" | "descending") {
    this.engine = new SelectionSortEngine(values, sortDirection);
  }

  getView(): SortViewState {
    return toView(this.engine.getState());
  }

  getTrace(): AlgorithmOperation[] {
    return this.engine.getTrace();
  }

  handlePrimaryAction(nearestIndex: number | null): SortActionOutcome {
    const state = this.engine.getState();
    if (state.isComplete || state.awaitingDecision || nearestIndex === null) {
      return { kind: "ignored" };
    }
    if (state.scanIndex !== nearestIndex) {
      return { kind: "rejected-wrong-box", expectedIndex: state.scanIndex };
    }
    const result = this.engine.inspect(performance.now());
    if (!result) {
      return { kind: "ignored" };
    }
    return { kind: "accepted", result: { view: toView(result.state), operations: result.operations } };
  }

  handleDecision(decision: SortDecision): SortActionOutcome {
    const before = this.engine.getState();
    if (!before.awaitingDecision) {
      return { kind: "ignored" };
    }
    const choice: "select" | "skip" = decision === "advance" ? "select" : "skip";
    const { wasCorrect, result } = this.engine.decide(choice, performance.now());
    if (!wasCorrect || !result) {
      return { kind: "rejected-wrong-decision" };
    }
    return { kind: "accepted", result: { view: toView(result.state), operations: result.operations } };
  }
}
